import { Elena, html, unsafeHTML } from "@elenajs/core";

/** ░█ [ELENA]: FAQ (Elementor style) */
export default class ElenaFaqUltimate extends Elena(HTMLElement) {
	static tagName = "origin-faq-ultimate";
	static props = [{ name: "items", reflect: false }, "openIndex", "openindex"];
	static observedAttributes = ["items", "openindex"];

	constructor() {
		super();

		if (this.openIndex === undefined || this.openIndex === null || Number.isNaN(Number(this.openIndex))) {
			this.openIndex = parseInt(this.getAttribute("openindex"), 10) || 0;
		}
	}

	parseItems(value) {
		if (Array.isArray(value)) return value;
		if (!value) return [];
		if (typeof value === "object" && value !== null && Array.isArray(value.items)) {
			return value.items;
		}
		if (typeof value === "string") {
			try {
				const parsed = JSON.parse(value);
				return Array.isArray(parsed) ? parsed : [];
			} catch {
				return [];
			}
		}
		return [];
	}

	normalizeItems(value) {
		if (Array.isArray(value)) return value;
		if (typeof value === "object" && value !== null && Array.isArray(value.items)) {
			return value.items;
		}
		return this.parseItems(value);
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === "items") {
			this.items = this.parseItems(newValue);
		} else if (name === "openindex") {
			this.openIndex = parseInt(newValue, 10) || 0;
		}
	}

	connectedCallback() {
		if (super.connectedCallback) super.connectedCallback();
		// Use a stable script id so JSON-LD is easy to find in the head
		if (!this._ldScriptId) this._ldScriptId = `origin-faq-ultimate-ld`;
		const itemsToRender = this.normalizeItems(this.items);
		if (itemsToRender.length) {
			this._updateJsonLd(itemsToRender);
		}
	}

	firstUpdated() {
		this._attachAccordionHandlers();
	}

	updated() {
		const itemsToRender = this.normalizeItems(this.items);
		if (itemsToRender.length) {
			this._updateJsonLd(itemsToRender);
		}
	}

	toggleItem(index) {
		const root = this._root || this.shadowRoot || this;
		const items = root.querySelectorAll(".e-n-accordion-item");
		items.forEach((el, i) => {
			el.open = i === index && !el.open;
		});
	}

	_attachAccordionHandlers() {
		if (this._accordionDelegateAttached) return;
		const root = this._root || this.shadowRoot || this;
		if (!root) return;
		this._accordionDelegateAttached = true;
		root.addEventListener("click", (ev) => {
			const summary = ev.target.closest(".e-n-accordion-item-title");
			if (!summary) return;
			ev.preventDefault();
			const index = Number(summary.dataset.accordionIndex);
			if (!Number.isNaN(index)) this.toggleItem(index);
		});
		root.addEventListener("keydown", (ev) => {
			if (ev.key !== "Enter" && ev.key !== " ") return;
			const summary = ev.target.closest(".e-n-accordion-item-title");
			if (!summary) return;
			ev.preventDefault();
			const index = Number(summary.dataset.accordionIndex);
			if (!Number.isNaN(index)) this.toggleItem(index);
		});
	}

	// Generate JSON-LD object for FAQPage from items
	_generateJsonLd(items) {
		const mainEntity = (items || []).map((it) => {
			const text = this._stripHtml((it && it.answer) || "");
			return {
				"@type": "Question",
				name: (it && it.question) || "",
				acceptedAnswer: {
					"@type": "Answer",
					text,
				},
			};
		});

		return {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity,
		};
	}

	// Insert or update script[type=application/ld+json] in document.head
	_updateJsonLd(items) {
		try {
			const ld = this._generateJsonLd(items || []);
			const json = JSON.stringify(ld, null, 2);
			let script = document.getElementById(this._ldScriptId);
			if (!script) {
				script = document.createElement("script");
				script.type = "application/ld+json";
				script.id = this._ldScriptId;
				document.head.appendChild(script);
			}
			script.textContent = json;
		} catch (e) {
			// fail silently
			console.warn("faq-ultimate: failed to update JSON-LD", e);
		}
	}

	_stripHtml(html) {
		if (!html) return "";
		return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
	}

	render() {
		const itemsToRender = this.normalizeItems(this.items);
		if (!this._ldScriptId) this._ldScriptId = `origin-faq-ultimate-ld-${Math.random().toString(36).slice(2,9)}`;
		this._updateJsonLd(itemsToRender);

		if (!itemsToRender.length) {
			return html`<div class="e-n-accordion"><p>No FAQ data available.</p></div>`;
		}

		return html`
			<div class="e-n-accordion" aria-label="Accordion. Open links with Enter or Space, close with Escape, and navigate with Arrow Keys">
				${itemsToRender.map((item, index) => {
					const isOpen = index === this.openIndex;
					return html`
						<details id="e-n-accordion-item-${index}" class="e-n-accordion-item" ?open=${isOpen}>
							<summary class="e-n-accordion-item-title" data-accordion-index="${index}" tabindex="0" aria-expanded="${isOpen}" aria-controls="e-n-accordion-item-${index}">
								<span class="e-n-accordion-item-title-header"><h3 class="e-n-accordion-item-title-text">${item.question}</h3></span>
								<span class="e-n-accordion-item-title-icon">
									<span class="e-opened">${this.iconMinus()}</span>
									<span class="e-closed">${this.iconPlus()}</span>
								</span>
							</summary>
							<div role="region" aria-labelledby="e-n-accordion-item-${index}" class="elementor-element elementor-element-content e-con-full e-flex e-con e-child">
								<div class="elementor-element elementor-widget elementor-widget-text-editor">
									<div class="e-n-accordion-content">${unsafeHTML(item.answer)}</div>
								</div>
							</div>
						</details>
					`;
				})}
			</div>
		`;
	}

	iconPlus() {
		return html`<svg aria-hidden="true" class="e-font-icon-svg e-fas-plus" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg"><path d="M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"></path></svg>`;
	}

	iconMinus() {
		return html`<svg aria-hidden="true" class="e-font-icon-svg e-fas-minus" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg"><path d="M416 208H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"></path></svg>`;
	}
}

ElenaFaqUltimate.define();

/*
Example usage with a custom items array:

const faqItems = [
  {
    question: "What should you do before using the component?",
    answer: "<p>Make sure the component is registered and the data is passed as an array of objects with <code>question</code> and <code>answer</code> fields.</p>",
  },
  {
    question: "How do you pass FAQ data from JavaScript?",
    answer: "<p>Create an array and assign it to the component instance's <code>items</code> property after the element exists in the DOM.</p>",
  },
  {
    question: "Can HTML be used inside the answer?",
    answer: "<p>Yes. The component supports HTML in the <code>answer</code> field through <code>unsafeHTML</code>.</p>",
  },
  {
    question: "What does the <code>openIndex</code> property do?",
    answer: "<p>It sets which accordion item is opened on render. If not set, all items stay closed.</p>",
  },
  {
    question: "How can I change the component behavior without editing its source?",
    answer: "<p>Pass a different <code>items</code> array from outside or update the existing component instance programmatically.</p>",
  },
];

<origin-faq-ultimate></origin-faq-ultimate>

// Safe JS usage example:
// window.addEventListener('DOMContentLoaded', () => {
//   const faq = document.querySelector('origin-faq-ultimate');
//   if (faq) {
//     faq.items = faqItems;
//   }
// });
*/
